import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";
import MultiSelectTrigger from "./MultiSelectTrigger.jsx";
import SelectMenu from "./SelectMenu.jsx";
import { normalizeOptions } from "./utils.js";

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 */

/**
 * MultiSelect handles dropdown rendering when multiple options selection is enabled.
 * It renders chip/tag triggers and checkboxes in the list options.
 *
 * @component
 * @param {Object} props
 * @param {Array<SelectOption | string | number>} props.options - Array of option objects or primitives.
 * @param {Array<string | number>} [props.value] - Array of selected option values.
 * @param {(values: Array<string | number>, options?: Array<SelectOption>) => void} [props.onChange] - Callback fired when options are selected/toggled.
 * @param {string} [props.placeholder] - Custom placeholder text (defaults to "Select...").
 * @param {string} [props.label] - Optional header label text above the field.
 * @param {boolean} [props.disabled] - Whether the select component is disabled.
 * @param {boolean | string} [props.error] - If boolean/string, styles field as invalid. If string, displays it as helper error text.
 * @param {string} [props.helperText] - Optional helper/hint text displayed below the field.
 * @param {boolean} [props.fullWidth] - If true, takes 100% width of parent container (defaults to false).
 * @param {boolean} [props.searchable] - If true, enables inline search inside the trigger value box.
 * @param {boolean} [props.menuPortal] - If true, the dropdown menu is rendered in a portal with fixed positioning. Defaults to false.
 * @param {string} [props.className] - External class name to style the container.
 * @param {Object} [props.classes] - Custom classes for inner components.
 * @param {string} [props.classes.root] - Custom class for root container.
 * @param {string} [props.classes.label] - Custom class for label text.
 * @param {string} [props.classes.trigger] - Custom class for trigger button/input.
 * @param {string} [props.classes.menu] - Custom class for menu list container.
 * @param {string} [props.classes.option] - Custom class for option list items.
 * @param {React.CSSProperties} [props.style] - Inline style for container element.
 * @param {string} [props.name] - HTML name attribute for form support.
 * @param {string} [props.id] - Unique ID for the wrapper element.
 * @param {boolean} [props.chip] - Enable chip mode rendering.
 * @param {boolean} [props.chips] - Enable chip mode rendering (alias).
 * @param {() => void} [props.onLoadMore] - Callback triggered when the options menu is scrolled to bottom.
 * @param {boolean} [props.hasMore] - If true, triggers onLoadMore on scroll and displays a spinner.
 * @param {boolean} [props.isLoadingMore] - If true, displays loading indicator at list bottom.
 * @param {string | number} [props.limitWidth] - The max-width of the menu in menu portal mode. Defaults to "450px".
 * @param {boolean} [props.simulateNativeOnChange] - If true, calls onChange with a simulated native event { target: { name, value } }.
 * @param {React.ComponentType | React.ReactElement} [props.arrowIcon] - Custom icon component or element to replace the default dropdown arrow.
 * @param {boolean} [props.floatingLabel] - If true, renders the label as a floating label that animates above the trigger when focused or a value is selected.
 * @param {string} [props.tooltip] - Tooltip text shown (with a short hover delay) when hovering over the trigger.
 * @param {boolean} [props.tooltipLight] - If true, (white background, dark text).
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  label,
  disabled = false,
  error = false,
  helperText,
  fullWidth = false,
  searchable = false,
  menuPortal = false,
  limitWidth = "450px",
  className: externalClassName,
  classes = {},
  style,
  name,
  id,
  chip = false,
  chips = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  simulateNativeOnChange = false,
  arrowIcon,
  floatingLabel = false,
  tooltip,
  tooltipLight = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchValue, setSearchValue] = useState("");
  const [menuPos, setMenuPos] = useState(null);

  const containerRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const isChipMode = chip || chips;

  // Normalize static options to standard objects
  const normalizedOptions = useMemo(() => {
    return normalizeOptions(options);
  }, [options]);

  // Ensure value is always an array
  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === "") return [];
    return [value];
  }, [value]);

  // Anchor dropdown menu to trigger with fixed position when portaling.
  const updateMenuPos = useCallback(() => {
    const anchor = triggerRef.current || containerRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    
    let top = r.bottom; // default below
    let left = r.left;
    let width = r.width;
    let maxHeight = undefined;

    if (menuRef.current) {
      const menuHeight = menuRef.current.offsetHeight || 150;
      const viewportHeight = window.innerHeight;
      
      const spaceBelow = viewportHeight - r.bottom - 10;
      maxHeight = Math.max(150, spaceBelow);
      const clampedMenuHeight = Math.min(menuHeight, maxHeight);
      
      // Check boundaries and adjust to fit viewport
      if (top < 10) {
        top = 10;
      }
      if (top + clampedMenuHeight > viewportHeight - 10) {
        top = Math.max(10, viewportHeight - clampedMenuHeight - 10);
      }
    }
    
    setMenuPos({ left, top, width, maxHeight });
  }, []);

  const portalTarget =
    menuPortal && typeof document !== "undefined"
      ? (triggerRef.current && triggerRef.current.closest("dialog")) || document.body
      : null;

  useEffect(() => {
    if (!menuPortal || !isOpen) return undefined;
    
    updateMenuPos();
    const rafId = requestAnimationFrame(() => {
      updateMenuPos();
    });
    
    const reposition = () => updateMenuPos();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [menuPortal, isOpen, updateMenuPos]);

  // Clean search states when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
    }
  }, [isOpen]);

  // Resolve options to display in list (handles local search state)
  const displayedOptions = useMemo(() => {
    if (!searchValue) {
      return normalizedOptions;
    }
    const query = searchValue.toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(query)
    );
  }, [normalizedOptions, searchValue]);

  // Find currently selected options
  const selectedOptions = useMemo(() => {
    return normalizedOptions.filter((opt) => selectedValues.includes(opt.value));
  }, [normalizedOptions, selectedValues]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current && containerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inContainer && !inMenu) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen]);

  // Set keyboard active index
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  // Auto-scroll hovered option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const listEl = listRef.current;
      const optionEl = listEl.children[focusedIndex];
      if (optionEl) {
        const listRect = listEl.getBoundingClientRect();
        const optionRect = optionEl.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) {
          listEl.scrollTop += optionRect.bottom - listRect.bottom;
        } else if (optionRect.top < listRect.top) {
          listEl.scrollTop -= listRect.top - optionRect.top;
        }
      }
    }
  }, [focusedIndex, isOpen]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleArrowClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      setIsOpen((prev) => !prev);
      if (!isOpen) {
        if (searchable && isChipMode && inputRef.current) {
          inputRef.current.focus();
        } else if (triggerRef.current) {
          triggerRef.current.focus();
        }
      }
    },
    [disabled, isOpen, searchable, isChipMode]
  );

  const handleInputClick = useCallback(
    (e) => {
      if (disabled) return;
      if (!isOpen) {
        setIsOpen(true);
      }
      if (searchable && isChipMode && inputRef.current) {
        inputRef.current.focus();
      }
    },
    [disabled, isOpen, searchable, isChipMode]
  );

  const handleInputFocus = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
    setIsOpen(true);
  }, []);

  const handleSelect = useCallback(
    (val) => {
      const option = normalizedOptions.find((opt) => opt.value === val);
      if (!option || option.disabled) return;

      let nextValues;
      if (selectedValues.includes(val)) {
        nextValues = selectedValues.filter((v) => v !== val);
      } else {
        nextValues = [...selectedValues, val];
      }

      const nextOptions = normalizedOptions.filter((opt) => nextValues.includes(opt.value));
      if (simulateNativeOnChange) {
        onChange?.({
          target: {
            name,
            value: nextValues,
          },
          preventDefault: () => {},
          stopPropagation: () => {},
        }, nextOptions);
      } else {
        onChange?.(nextValues, nextOptions);
      }
    },
    [normalizedOptions, selectedValues, onChange, simulateNativeOnChange, name]
  );

  const handleClearSearch = useCallback((e) => {
    e.stopPropagation();
    setSearchValue("");
    if (searchable && isChipMode && inputRef.current) {
      inputRef.current.focus();
    } else if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [searchable, isChipMode]);

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;

      if (e.key === "Backspace" && searchable && isChipMode && !searchValue && selectedValues.length > 0) {
        // Remove last selected value
        const lastVal = selectedValues[selectedValues.length - 1];
        handleSelect(lastVal);
        return;
      }

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            const opt = displayedOptions[focusedIndex];
            if (!opt.disabled) {
              handleSelect(opt.value);
            }
          }
          break;
        case " ":
          // Don't toggle selection if typing space in search trigger input
          if (!searchable || e.target !== triggerRef.current && e.target !== inputRef.current) {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
              const opt = displayedOptions[focusedIndex];
              if (!opt.disabled) {
                handleSelect(opt.value);
              }
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          if (triggerRef.current) {
            triggerRef.current.blur();
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setFocusedIndex((prev) => {
              let nextIdx = prev + 1;
              while (nextIdx < displayedOptions.length && displayedOptions[nextIdx].disabled) {
                nextIdx++;
              }
              return nextIdx < displayedOptions.length ? nextIdx : prev;
            });
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setFocusedIndex((prev) => {
              let nextIdx = prev - 1;
              while (nextIdx >= 0 && displayedOptions[nextIdx].disabled) {
                nextIdx--;
              }
              return nextIdx >= 0 ? nextIdx : prev;
            });
          }
          break;
        case "Tab":
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [disabled, isOpen, focusedIndex, displayedOptions, handleSelect, searchable, isChipMode, searchValue, selectedValues]
  );

  const handleScroll = useCallback((e) => {
    if (!hasMore || isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      onLoadMore?.();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const containerClassName = getClassName(
    styles.container,
    "select-container",
    {
      [styles.fullWidth]: fullWidth,
      "select-full-width": fullWidth,
      [styles.containerFloatingLabel]: !!floatingLabel,
    },
    classes.root,
    externalClassName,
  );

  const isFloating = isOpen || (selectedValues && selectedValues.length > 0);

  const labelClassName = getClassName(
    floatingLabel ? styles.labelFloating : styles.label,
    floatingLabel ? "select-label-floating" : "select-label",
    {
      [styles.labelError]: !floatingLabel && error,
      "select-label-error": !floatingLabel && error,
      [styles.labelFloatingActive]: !!floatingLabel && isFloating,
      "select-label-floating-active": !!floatingLabel && isFloating,
      [styles.labelFloatingError]: !!floatingLabel && error,
      "select-label-floating-error": !!floatingLabel && error,
      [styles.labelFloatingDisabled]: !!floatingLabel && disabled,
      "select-label-floating-disabled": !!floatingLabel && disabled,
    },
    classes.label,
  );

  const triggerClassName = getClassName(
    styles.trigger,
    "select-trigger",
    {
      [styles.triggerChips]: isChipMode,
      "select-trigger-chips": isChipMode,
      [styles.triggerSearchable]: searchable && !isChipMode,
      "select-trigger-searchable": searchable && !isChipMode,
      [styles.triggerOpen]: isOpen,
      "select-trigger-open": isOpen,
      [styles.triggerError]: error,
      "select-trigger-error": error,
    },
    classes.trigger,
  );

  const isErrorText = typeof error === "string" ? error : null;
  const bottomText = isErrorText || helperText;

  // Selected values labels joined by comma (for non-chip mode)
  const selectionDisplay = useMemo(() => {
    if (selectedOptions.length === 0) return "";
    return selectedOptions.map((opt) => opt.label).join(", ");
  }, [selectedOptions]);

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={style}
      id={id}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...rest}
    >
      {label && !floatingLabel && <label className={labelClassName}>{label}</label>}
      <div className={getClassName(styles.selectWrapper, "select-wrapper")}>
        {label && floatingLabel && <label className={labelClassName}>{label}</label>}
        {label && floatingLabel && <span className={styles.labelGhost} aria-hidden="true">{label}</span>}
        <MultiSelectTrigger
          searchable={searchable}
          isOpen={isOpen}
          searchValue={searchValue}
          selectedOptions={selectedOptions}
          placeholder={(floatingLabel && !isFloating) ? "" : placeholder}
          disabled={disabled}
          triggerClassName={triggerClassName}
          triggerRef={triggerRef}
          inputRef={inputRef}
          isChipMode={isChipMode}
          selectionDisplay={selectionDisplay}
          handleInputChange={handleInputChange}
          handleInputClick={handleInputClick}
          handleInputFocus={handleInputFocus}
          handleKeyDown={handleKeyDown}
          handleToggle={handleToggle}
          handleArrowClick={handleArrowClick}
          handleClearSearch={handleClearSearch}
          handleSelect={handleSelect}
          arrowIcon={arrowIcon}
          tooltip={tooltip}
          tooltipLight={tooltipLight}
        />

        <SelectMenu
          isOpen={isOpen}
          menuRef={menuRef}
          listRef={listRef}
          classes={classes}
          menuPortal={menuPortal}
          menuPos={menuPos}
          portalTarget={portalTarget}
          isLoading={false}
          displayedOptions={displayedOptions}
          selectedValues={selectedValues}
          focusedIndex={focusedIndex}
          handleSelect={handleSelect}
          setFocusedIndex={setFocusedIndex}
          handleScroll={handleScroll}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          multiple={true}
          limitWidth={limitWidth}
        />
      </div>

      {name && selectedValues.map((v) => (
        <input
          key={v}
          type="hidden"
          name={name}
          value={String(v)}
        />
      ))}
      <input
        aria-hidden="true"
        tabIndex={-1}
        name={name ? `${name}_validation` : undefined}
        required={rest.required}
        value={selectedValues.length > 0 ? "filled" : ""}
        onChange={() => {}}
        style={{
          opacity: 0,
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "1px",
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      {bottomText && (
        <div
          className={getClassName(
            styles.helperText,
            "select-helper-text",
            {
              [styles.errorText]: isErrorText,
              "select-error-text": isErrorText,
            }
          )}
        >
          {bottomText}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
